<script setup lang="tsx">
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import EditDeleteButtons from '../widgets/EditDeleteButtons.vue'
import CancelSaveButtons from '../widgets/CancelSaveButtons.vue'
import sliderStore from '@/stores/slider'
import toastStore from '@/stores/toast'
import http from '@/services/http'
import type { CustomFieldDefinition, Instance, Response } from '@/models/api-generated'
import { instanceRefreshCallback } from '@/components/instance/shared'
import { genRouteID, hasPermission } from '@/services/utils'
import { PermAdminInstances } from '@/models/enums-generated'
import { fetchCustomValues, type CustomValueMap } from '@/services/custom'
import CustomValueEdit from '../custom_field/CustomValueEdit.vue'
import CustomValueDisplay from '../custom_field/CustomValueDisplay.vue'

const props = defineProps<{ instanceId: number }>()
const { t } = useI18n()
const slider = sliderStore()
const toaster = toastStore()
const isAdd = computed(() => props.instanceId === 0)
const isEdit = ref(false)
const isLoading = ref(false)
const instance = ref<Instance>(defaultInstance())
const isChangeMode = computed(() => isAdd.value || isEdit.value)
const canSave = computed(() => !isLoading.value && instance.value.name.trim().length > 0)
const canDelete = computed(() => hasPermission(PermAdminInstances))
const canEdit = computed(() => hasPermission(PermAdminInstances))
const custom = ref<CustomFieldDefinition[]>([])
const customValues = ref<CustomValueMap>()

onMounted(async () => {
	await refresh()
	slider.watchChanges(instance)
})

watch(() => props.instanceId, refresh)
watch(isEdit, (edit) => slider.setFooter(edit ? editBtns : viewBtns))

const editBtns = <CancelSaveButtons saveId="save-instance" cancelId="cancel-instance" canSave={canSave} onCancel={cancel} onSave={save} />
const viewBtns = (
	<EditDeleteButtons
		editId="edit-instance"
		deleteId="delete-instance"
		canEdit={canEdit}
		onEdit={edit}
		canDelete={canDelete}
		onDelete={remove}
	/>
)

function defaultInstance(): Instance {
	return {
		id: 0,
		name: '',
		description: '',
		address: '',
		email: '',
		phone: '',
		contact: '',
		createdBy: 0,
		mainInstance: 0,
	}
}

async function edit() {
	isEdit.value = true
}

async function refresh() {
	isEdit.value = false
	if (!isAdd.value) {
		const { data } = await http.get<Response<Instance>>(`/api/instance/${props.instanceId}`)
		if (data.data) instance.value = data.data
		slider.setFooter(viewBtns)
	} else {
		instance.value = defaultInstance()
		slider.setFooter(editBtns)
	}
	await getCustomFields()
}

async function getCustomFields() {
	const { data } = await http.get<Response<CustomFieldDefinition[]>>(`api/customFields/instances`)
	if (data.data?.length) custom.value.push(...data.data)
	customValues.value = await fetchCustomValues('instances', props.instanceId)
}

function updateCustomValue(fieldId: number, value: string | undefined) {
	if (!value) return
	customValues.value ??= {}
	customValues.value[fieldId] ??= {}
	customValues.value[fieldId][instance.value.id] = value
}

async function cancel() {
	const doCancel = async () => {
		if (isAdd.value) {
			slider.close()
		} else {
			await refresh()
		}
	}

	if (slider.getHasUnsavedChanges()) {
		slider.requestCancel(doCancel)
	} else {
		await doCancel()
	}
}

function remove() {
	slider.requestDeleteConfirm(t('misc.confirmDelete'), 'instance', confirmDelete)
}

async function confirmDelete() {
	const { status } = await http.get(`/api/instance/delete/${genRouteID(instance.value.id, '', 'Instance')}`)
	if (status === 200) {
		toaster.showSuccess(t('misc.successfullyDeleted'))
		slider.setUnsavedChanges(false)
		slider.close()
		if (instanceRefreshCallback.value) instanceRefreshCallback.value()
	}
}

async function save() {
	const data = instance.value

	const url = `/api/instance`

	isLoading.value = true
	const { status } = await http.post<Response<Instance>>(url, data)
	if (status === 200) {
		toaster.showSuccess(t(isAdd.value ? 'misc.successfullyAdded' : 'misc.successfullyEdited'))
		slider.setUnsavedChanges(false)
		slider.close()
		if (instanceRefreshCallback.value) instanceRefreshCallback.value()
	}
	isLoading.value = false
}
</script>

<template>
	<div :class="{ striped: !isChangeMode }">
		<div
			class="row"
			v-for="(field, label) in {
				name: t('misc.name'),
				address: t('address'),
				email: t('email'),
				phone: t('phone'),
				contact: t('contact'),
				description: t('description'),
			}"
			:key="label"
		>
			<label class="col-4 col-form-label">{{ field }}</label>
			<div class="col-8 d-flex justify-content-end align-items-center">
				<template v-if="isChangeMode">
					<input
						v-if="label !== 'description'"
						class="form-control mb-2"
						:data-test="'instance-' + label.toLowerCase()"
						v-model="instance[label]"
					/>
					<textarea
						v-else
						class="form-control mb-2 w-100"
						data-test="instance-description"
						v-model="instance.description"
						maxlength="500"
					></textarea>
				</template>
				<span v-else>{{ instance[label] || '-' }}</span>
			</div>
		</div>

		<template v-if="!isChangeMode">
			<template v-for="field in custom" :key="field.id">
				<div class="row mt-2 text-wrap">
					<div class="col">{{ field.label }}</div>
					<div class="col d-flex justify-content-end align-items-center text-break">
						<CustomValueDisplay :field="field" :value="customValues?.[field.id]?.[instance.id] ?? ''" />
					</div>
				</div>
			</template>
		</template>
		<template v-else>
			<template v-for="field in custom" :key="field.id">
				<div class="row mt-2 text-wrap">
					<div class="col">{{ field.label }}</div>
					<div class="col d-flex justify-content-end align-items-center text-break">
						<CustomValueEdit
							:field="field"
							:modelValue="customValues?.[field.id]?.[instance.id] ?? ''"
							@update:modelValue="(val) => updateCustomValue(field.id, val)"
						/>
					</div>
				</div>
			</template>
		</template>
	</div>
</template>

<style lang="sass" scoped>
.fixed-badge
  max-height: 1.5rem
</style>
